/**
 * DPS coverage readout + override-conflict list (spec 07-COV §5), rendered on the
 * eval page beside the existing case-coverage element. Property vocabulary
 * throughout; every readout names its base so it cannot be cross-read against the
 * case-coverage percentage (§4.4).
 *
 * Empty states render NOTHING (zero unscheduled, zero unvalued, zero conflicts →
 * no rows, no panel, no banner). The visual design is UX-owed; this is the
 * functional surface that consumes the CoverageCounts / OverrideConflict seams.
 */

import { useState } from 'react';
import { Link } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import { MILESTONE_LABELS } from '../schedule-dialog/constants';
import type {
  CoverageCounts,
  OverrideConflict,
} from '../schedule-dialog/types';
import {
  AffectedSlotsDrillIn,
  type AffectedSlotsFilter,
} from './affected-slots-drill-in';
import {
  conflictSentence,
  coverageBannerSubtitle,
  residualBannerClauses,
} from './copy';
import styles from './coverage-conflicts.module.scss';

interface CoverageConflictsProps {
  coverage?: CoverageCounts;
  overrideConflicts?: OverrideConflict[];
  /**
   * The viewed calculation's id — required to open the in-page drill-in behind a
   * readout's "View" (it fetches `/evaluations/calculations/:id/unscheduled_slots`).
   * Absent on a draft / non-DPS run, where no drill-in is offered.
   */
  calculationId?: number;
}

export const CoverageConflicts = ({
  coverage,
  overrideConflicts,
  calculationId,
}: CoverageConflictsProps) => {
  const conflicts = overrideConflicts ?? [];
  const unscheduled = coverage?.unscheduledCount ?? 0;
  const unvalued = coverage?.unvaluedCount ?? 0;
  const hasReadout = unscheduled > 0 || unvalued > 0;

  // Which readout's "View" drill-in is open (null = none). Only one at a time.
  const [openDrill, setOpenDrill] = useState<AffectedSlotsFilter | null>(null);

  // Empty is not an error anywhere in this story — render nothing at all.
  if (!hasReadout && conflicts.length === 0) {
    return null;
  }

  // The banner carries only causes WITHOUT a readout line (override conflicts) —
  // the unscheduled/unvalued readouts below already state theirs, base-named and
  // with a drill-in, so restating them here is the duplicate the reporter flagged
  // (INS-12496 defect B).
  const bannerClauses = residualBannerClauses(conflicts);
  // The drill-in fetches by calculation id, and is only meaningful when the server
  // advertised the affected-slots endpoint (a DPS run). Gate the "View" on both.
  const canDrillIn =
    calculationId !== undefined && coverage?.affectedSlotsHref !== undefined;

  const viewLink = (filter: AffectedSlotsFilter, label: string) =>
    canDrillIn && (
      <Link
        as="button"
        type="button"
        aria-label={label}
        onClick={() => setOpenDrill(filter)}
      >
        View
      </Link>
    );

  return (
    <section
      className={styles.container}
      aria-label="Development Pace coverage"
    >
      {bannerClauses.length > 0 && (
        <p className={styles.banner}>{coverageBannerSubtitle(bannerClauses)}</p>
      )}

      {hasReadout && coverage && (
        <div className={styles.readout}>
          {unscheduled > 0 && (
            <p className={styles.readoutLine}>
              <FormattedCount value={coverage.unscheduledCount} /> of{' '}
              <FormattedCount value={coverage.paceableInventoryCount} />{' '}
              paceable properties unscheduled{' '}
              {viewLink('unscheduled', 'View unscheduled properties')}
            </p>
          )}
          {/* Names its measure ("in this evaluation") so it reads as a pricing
              outcome, not a gap in the case-coverage % above it (§4.4) — but
              deliberately NOT "of N paceable", because unvaluedCount is counted
              across every priced slot (incl. PDP via the calculator's economic
              drop reasons), so it is not a subset of the paceable base. For the
              same reason the drill-in below (paceable-only) may list fewer. */}
          {unvalued > 0 && (
            <p className={styles.readoutLine}>
              <FormattedCount value={unvalued} /> properties will not be valued
              in this evaluation{' '}
              {viewLink('unvalued', 'View properties that will not be valued')}
            </p>
          )}
        </div>
      )}

      {openDrill !== null && calculationId !== undefined && (
        <AffectedSlotsDrillIn
          calculationId={calculationId}
          filter={openDrill}
          onClose={() => setOpenDrill(null)}
        />
      )}

      {conflicts.length > 0 && (
        <table className={styles.conflicts} aria-label="Override conflicts">
          <thead>
            <tr>
              <th className={styles.conflictHeaderCell}>Property</th>
              <th className={styles.conflictHeaderCell}>Milestone</th>
              <th className={styles.conflictHeaderCell}>Override</th>
              <th className={styles.conflictHeaderCell}>Conflict</th>
            </tr>
          </thead>
          <tbody>
            {conflicts.map((conflict) => (
              <tr key={`${conflict.slotId}-${conflict.field}`}>
                <td className={styles.conflictCell}>{conflict.slotId}</td>
                <td className={styles.conflictCell}>
                  {MILESTONE_LABELS[conflict.field]}
                </td>
                <td className={styles.conflictCell}>
                  {conflict.overrideValue}
                </td>
                <td className={styles.conflictCell}>
                  {conflictSentence(conflict.conflict)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
